/**
 * client/src/help/routes.ts
 *
 * Path helpers for Help (ST-00200): normalising app paths, matching a path against a route
 * pattern (`/lexistar/trips/:id`), and the rule the "?" panel uses to find the guides for the
 * page the user is on — exact match first, then the longest matching prefix (so a detail page
 * such as /lexistar/trips/42 falls back to the guides about /lexistar/trips).
 */

/** Drop ?query / #hash, collapse repeated slashes and a trailing slash (root stays "/"). */
export function normalizePath(path: string): string {
  const bare = (path.split(/[?#]/, 1)[0] ?? '').replace(/\/{2,}/g, '/');
  if (!bare || bare === '/') return '/';
  const withSlash = bare.startsWith('/') ? bare : `/${bare}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

export const pathSegments = (path: string): string[] => normalizePath(path).split('/').filter(Boolean);

export const isParamSegment = (segment: string): boolean => segment.startsWith(':');

/** True for a route pattern (`:param` or `*` segments) rather than one concrete path. */
export const isRoutePattern = (path: string): boolean =>
  pathSegments(path).some((s) => isParamSegment(s) || s === '*');

export type RouteMatch = 'exact' | 'prefix';

/**
 * Does `pattern` match `path` segment by segment? `exact` when every segment of both matches,
 * `prefix` when the pattern covers the first segments of a longer path, else null. `:param`
 * matches any one segment; a `*` segment matches the rest. The root pattern "/" only ever
 * matches "/" exactly (it would otherwise be a prefix of everything).
 */
export function matchRoute(pattern: string, path: string): RouteMatch | null {
  const pat = pathSegments(pattern);
  const segs = pathSegments(path);
  if (pat.length === 0) return segs.length === 0 ? 'exact' : null;
  for (let i = 0; i < pat.length; i++) {
    const p = pat[i] as string;
    if (p === '*') return 'exact';
    const s = segs[i];
    if (s === undefined) return null;
    if (!isParamSegment(p) && p !== s) return null;
  }
  return pat.length === segs.length ? 'exact' : 'prefix';
}

/** Number of literal (non-parameter) segments — the more, the more specific the pattern. */
export const staticSegments = (pattern: string): number =>
  pathSegments(pattern).filter((s) => !isParamSegment(s) && s !== '*').length;

/**
 * A score for how well `pattern` fits `path` (0 = not at all): exact matches beat every prefix
 * match, longer prefixes beat shorter ones, literal segments beat parameters.
 */
export function routeScore(pattern: string, path: string): number {
  const m = matchRoute(pattern, path);
  if (!m) return 0;
  const len = pathSegments(pattern).length;
  const literal = staticSegments(pattern);
  return m === 'exact' ? 10000 + len * 10 + literal : len * 10 + literal;
}
